import { Modal, Form, Input, DatePicker, Select, Radio, Switch, Collapse } from "antd";
import { Bell, Repeat } from "lucide-react";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import RecurringTaskConfig from "./RecurringTaskConfig";
import useUsers from "../../hooks/useUsers";

const { TextArea } = Input;
const { Panel } = Collapse;

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pendiente" },
  { value: "IN_PROGRESS", label: "En Progreso" },
  { value: "COMPLETED", label: "Completada" },
  { value: "CANCELLED", label: "Cancelada" },
];

const PRIORITY_OPTIONS = [
  { value: "LOW", label: "Baja" },
  { value: "MEDIUM", label: "Media" },
  { value: "HIGH", label: "Alta" },
];

const COLOR_OPTIONS = [
  { value: "#8B5CF6", label: "Violeta", color: "#8B5CF6" },
  { value: "#F59E0B", label: "Naranja", color: "#F59E0B" },
  { value: "#10B981", label: "Verde", color: "#10B981" },
  { value: "#EF4444", label: "Rojo", color: "#EF4444" },
  { value: "#3B82F6", label: "Azul", color: "#3B82F6" },
];

function TaskFormModal({
  visible,
  task,
  initialDate,
  onClose,
  onSubmit,
  onSubmitRecurring,
  loading,
  users = [],
}) {
  const [form] = Form.useForm();
  const [isRecurring, setIsRecurring] = useState(false);
  const isEditing = !!task;
  const isRecurringTask = task?.isRecurring;

  // Reset recurring state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setIsRecurring(false);
    }
  }, [visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (isRecurring && onSubmitRecurring) {
        // Crear tarea periodica
        const recurringData = {
          title: values.title,
          description: values.description || "",
          frequency: values.frequency,
          interval: values.interval || 1,
          weekdays: values.weekdays || [],
          startDate: values.startDate.format("YYYY-MM-DD"),
          endDate: values.endDate.format("YYYY-MM-DD"),
          priority: values.priority,
          color: values.color,
          hasReminder: values.hasReminder || false,
          assignedTo: values.assignedTo || null,
        };
        await onSubmitRecurring(recurringData);
      } else {
        // Crear/editar tarea normal
        const data = {
          ...values,
          date: values.date ? values.date.format("YYYY-MM-DD") : null,
        };
        await onSubmit(data);
      }
      form.resetFields();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsRecurring(false);
    onClose();
  };

  // Valores iniciales
  const initialValues = task
    ? {
        ...task,
        date: task.date ? dayjs(task.date) : null,
        assignedTo: task.assignedTo?.id || null,
      }
    : {
        status: "PENDING",
        priority: "MEDIUM",
        color: "#8B5CF6",
        hasReminder: false,
        date: initialDate ? dayjs(initialDate) : null,
        startDate: initialDate ? dayjs(initialDate) : dayjs(),
        interval: 1,
        weekdays: [],
      };

  // Preparar opciones de usuarios
  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email,
  }));

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          {isRecurringTask && <Repeat size={18} className="text-blue-500" />}
          {isEditing ? "Editar Tarea" : isRecurring ? "Nueva Tarea Periodica" : "Nueva Tarea"}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isEditing ? "Guardar" : isRecurring ? "Crear Tarea Periodica" : "Crear"}
      cancelText="Cancelar"
      destroyOnClose
      width={550}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        preserve={false}
      >
        <Form.Item
          name="title"
          label="Titulo"
          rules={[{ required: true, message: "Ingresa un titulo" }]}
        >
          <Input placeholder="Titulo de la tarea" maxLength={200} />
        </Form.Item>

        <Form.Item name="description" label="Descripcion">
          <TextArea rows={3} placeholder="Descripcion o detalles (opcional)" />
        </Form.Item>

        {/* Selector de asignacion */}
        <Form.Item name="assignedTo" label="Asignar a">
          <Select
            options={userOptions}
            placeholder="Sin asignar"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {/* Campos de fecha - solo para tareas normales (no periodicas) */}
        {!isRecurring && (
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="date" label="Fecha (opcional)">
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                placeholder="Sin fecha"
                allowClear
              />
            </Form.Item>

            <Form.Item name="status" label="Estado">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </div>
        )}

        {isRecurring && (
          <Form.Item name="status" label="Estado inicial" hidden>
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Form.Item name="priority" label="Prioridad">
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>

          <Form.Item name="hasReminder" label="Recordatorio" valuePropName="checked">
            <Switch
              checkedChildren={<Bell size={14} />}
              unCheckedChildren="No"
            />
          </Form.Item>
        </div>

        <Form.Item name="color" label="Color">
          <Radio.Group className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((opt) => (
              <Radio.Button
                key={opt.value}
                value={opt.value}
                style={{
                  borderColor: opt.color,
                  backgroundColor:
                    form.getFieldValue("color") === opt.value
                      ? opt.color
                      : "transparent",
                }}
              >
                <span
                  className="inline-block w-4 h-4 rounded-full mr-1"
                  style={{ backgroundColor: opt.color }}
                />
                {opt.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        {/* Seccion de tarea periodica - solo al crear */}
        {!isEditing && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Switch
                checked={isRecurring}
                onChange={setIsRecurring}
                checkedChildren={<Repeat size={14} />}
                unCheckedChildren="No"
              />
              <span className="text-sm font-medium">Tarea periodica</span>
            </div>

            {isRecurring && <RecurringTaskConfig form={form} startDate={initialDate} />}
          </div>
        )}

        {/* Indicador de tarea periodica en modo edicion */}
        {isEditing && isRecurringTask && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 text-blue-700">
              <Repeat size={16} />
              <span className="text-sm">
                Esta tarea pertenece a una serie periodica
              </span>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
}

export default TaskFormModal;
